import { useContext, useMemo, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from 'lib/utils'
import { XIcon } from 'lucide-react'
import * as React from 'react'
import { Button } from './Button'

interface FullViewContextType {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

const FullViewContext = React.createContext<FullViewContextType>({
  isOpen: false,
  setIsOpen: () => {},
})

function FullViewProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const value = useMemo(() => ({ isOpen, setIsOpen }), [isOpen, setIsOpen])

  return <FullViewContext.Provider value={value}>{children}</FullViewContext.Provider>
}

function FullView({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return (
    <FullViewProvider>
      <FullViewContext.Consumer>
        {({ isOpen, setIsOpen }) => (
          <DialogPrimitive.Root
            open={isOpen}
            onOpenChange={setIsOpen}
            data-slot="full-view"
            {...props}
          />
        )}
      </FullViewContext.Consumer>
    </FullViewProvider>
  )
}

function FullViewTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="full-view-trigger" {...props} />
}

function FullViewPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="full-view-portal" {...props} />
}

function FullViewClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="full-view-close" {...props} />
}

function FullViewOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="full-view-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/80 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  )
}

function FullViewContent({
  className,
  children,
  footer,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  footer?: React.ReactNode
}) {
  const { setIsOpen } = useContext(FullViewContext)

  return (
    <FullViewPortal>
      <FullViewOverlay />
      <DialogPrimitive.Content
        data-slot="full-view-content"
        className={cn(
          'fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/10 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
          className
        )}
        {...props}
        onClick={e => {
          if (e.target === e.currentTarget) {
            setIsOpen(false)
          }
        }}
      >
        {children}
        {footer && <div className="absolute right-0 bottom-8 left-0 p-4">{footer}</div>}
        <FullViewClose className="absolute top-4 right-4 ring-offset-background">
          <Button variant="outline" size="icon">
            <XIcon />
            <span className="sr-only">Close</span>
          </Button>
        </FullViewClose>
      </DialogPrimitive.Content>
    </FullViewPortal>
  )
}

export {
  FullView,
  FullViewClose,
  FullViewContent,
  FullViewOverlay,
  FullViewPortal,
  FullViewTrigger,
  FullViewProvider,
}
